import { forwardRef, useMemo, useState } from 'react';

import { debounce, isEmpty } from 'lodash';
import { RiErrorWarningLine } from 'react-icons/ri';

import {
  Autocomplete as MantineAutocomplete,
  AutocompleteProps as MantineAutocompleteProps
} from '@mantine/core';

import { InputLoader } from '../../feedback';
import { useMergeClassNames } from '../../utils';
// Auto complete shares styles from Select
import { SelectOverrideClassNames } from '../select';

interface AsyncAutocompleteProps extends MantineAutocompleteProps {
  setValue: (value: string) => void;
  fieldValue: string | null;
  onSearch: (searchKey: string) => Promise<string[]>;
  includeDynamicOption?: boolean; // Whether to include the current input as an option
  afterSelection?: (selectedValue: string) => void; // Callback after selecting an existing option
  afterCustomSelection?: (customValue: string) => void; // Callback after selecting a custom option
}

// Minimum input value length to trigger the search
const MIN_SEARCH_LENGTH = 3;
// Time to wait before triggering the search
const SEARCH_DEBOUNCE_TIME = 300;

type Option = {
  label: string;
  value: string;
  customOption?: boolean;
};

const AsyncAutocomplete = forwardRef<HTMLInputElement, AsyncAutocompleteProps>(
  (
    {
      size = 'xl',
      leftSection,
      rightSection,
      error,
      onFocus,
      onBlur,
      onSearch,
      classNames,
      setValue,
      fieldValue,
      includeDynamicOption = false,
      afterSelection,
      afterCustomSelection,
      ...rest
    },
    ref
  ) => {
    const [focussed, setFocussed] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Option[]>([]);

    const rightSectionFinal = loading ? (
      <InputLoader size={size} />
    ) : error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : undefined;

    const classes = useMemo(() => {
      const classes = {
        ...SelectOverrideClassNames({
          size,
          focussed,
          error
        })
      };
      return classes;
    }, [size, focussed, error]);

    const currentValue = (() => {
      if (!fieldValue) {
        return;
      }
      const option = data.find((o) => o.value === fieldValue);
      if (option) {
        return option;
      }

      return {
        value: fieldValue,
        label: fieldValue,
        customOption: !!includeDynamicOption
      };
    })();

    const handleSearch = debounce(async (value: string) => {
      if (value.length < MIN_SEARCH_LENGTH) return setData([]);

      try {
        setLoading(true);
        const searchResult = await onSearch(value);
        const options: Option[] = searchResult.map((optionVal) => ({
          label: optionVal,
          value: optionVal
        }));

        if (isEmpty(options) && includeDynamicOption) {
          options.push({
            label: value,
            value: value,
            customOption: true
          });
        }

        setData(options);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_TIME);

    const handleOptionSelect = (value: string) => {
      if (!value) return;

      const option = data.find((o) => o.value === value);

      if (!option?.value) {
        setValue('');
        return;
      }

      if (includeDynamicOption && option?.customOption) {
        afterCustomSelection && afterCustomSelection(option?.value);
      } else {
        afterSelection && afterSelection(option?.value);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(false);
      handleOptionSelect(e?.target?.value ?? '');
      setData([]);
      onBlur && onBlur(e);
    };

    const handleChange = (value: string) => {
      setValue(value);
      handleSearch(value);
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    return (
      <MantineAutocomplete
        {...rest}
        ref={ref}
        data={data}
        value={currentValue?.value ?? ''}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onOptionSubmit={handleOptionSelect}
        size={size}
        leftSection={leftSection}
        rightSection={rightSectionFinal}
        error={error}
        classNames={mergedClassNames}
      />
    );
  }
);

export { AsyncAutocomplete };
export type { AsyncAutocompleteProps };
